// Recurrence expansion for RRULE-based series, with per-occurrence overrides.
//
// We store RRULE fragments (FREQ/BYDAY/INTERVAL only — no DTSTART/UNTIL) alongside a
// separate `timezone` and `recurrenceEndAt`. To expand correctly across DST transitions,
// we use the well-known "floating time" trick: convert the real UTC instant to a Date whose
// UTC-getters equal the local wall-clock time, run rrule.js against that (rrule always uses
// UTC getters internally), then convert the resulting wall-clock values back to real UTC
// instants for the target timezone.

import { RRule } from "rrule";
import { utcToFloating, floatingToUtc } from "./timezone";
import type { EventOccurrence, EventOverride, EventRecord } from "./types";

function toFloating(iso: string, timeZone: string, allDay: boolean): Date {
  if (allDay) return new Date(iso);
  return utcToFloating(new Date(iso), timeZone);
}

function floatingToReal(date: Date, timeZone: string, allDay: boolean): Date {
  if (allDay) return date;
  return floatingToUtc(date, timeZone);
}

export function expandEvent(
  event: EventRecord,
  overrides: EventOverride[],
  rangeStartIso: string,
  rangeEndIso: string,
): EventOccurrence[] {
  const durationMs = new Date(event.endAt).getTime() - new Date(event.startAt).getTime();
  const rangeStartMs = new Date(rangeStartIso).getTime();
  const rangeEndMs = new Date(rangeEndIso).getTime();

  if (!event.rrule) {
    const startMs = new Date(event.startAt).getTime();
    const endMs = new Date(event.endAt).getTime();
    if (endMs < rangeStartMs || startMs > rangeEndMs) return [];
    return [
      {
        ...event,
        occurrenceId: event.id,
        seriesId: null,
        occurrenceStart: event.startAt,
        isException: false,
      },
    ];
  }

  const overrideMap = new Map(overrides.map((o) => [o.occurrenceStart, o]));
  const localDtStart = toFloating(event.startAt, event.timezone, event.allDay);
  const localUntil = event.recurrenceEndAt
    ? toFloating(event.recurrenceEndAt, event.timezone, event.allDay)
    : undefined;

  let rule: RRule;
  try {
    rule = new RRule({
      ...RRule.parseString(event.rrule),
      dtstart: localDtStart,
      ...(localUntil ? { until: localUntil } : {}),
    });
  } catch {
    return [];
  }

  const localRangeStart = toFloating(rangeStartIso, event.timezone, event.allDay);
  const localRangeEnd = toFloating(rangeEndIso, event.timezone, event.allDay);
  const searchStart = new Date(localRangeStart.getTime() - Math.max(durationMs, 0) - 86400000);

  const localOccurrences = rule.between(searchStart, localRangeEnd, true);

  const results: EventOccurrence[] = [];
  for (const localOcc of localOccurrences) {
    const realStart = floatingToReal(localOcc, event.timezone, event.allDay);
    const realEnd = new Date(realStart.getTime() + durationMs);
    if (realEnd.getTime() < rangeStartMs || realStart.getTime() > rangeEndMs) continue;

    const occurrenceStartIso = realStart.toISOString();
    const override = overrideMap.get(occurrenceStartIso);
    if (override?.isCancelled) continue;

    results.push({
      ...event,
      occurrenceId: `${event.id}::${occurrenceStartIso}`,
      seriesId: event.id,
      occurrenceStart: occurrenceStartIso,
      isException: Boolean(override),
      title: override?.title ?? event.title,
      startAt: override?.startAt ?? occurrenceStartIso,
      endAt: override?.endAt ?? realEnd.toISOString(),
      locationName: override?.locationName ?? event.locationName,
      address: override?.address ?? event.address,
      notes: override?.notes ?? event.notes,
      opponent: override?.opponent ?? event.opponent,
    });
  }
  return results;
}

export function expandEvents(
  events: EventRecord[],
  overridesByEvent: Map<string, EventOverride[]>,
  rangeStartIso: string,
  rangeEndIso: string,
): EventOccurrence[] {
  const all: EventOccurrence[] = [];
  for (const event of events) {
    all.push(
      ...expandEvent(event, overridesByEvent.get(event.id) ?? [], rangeStartIso, rangeEndIso),
    );
  }
  all.sort((a, b) => a.occurrenceStart.localeCompare(b.occurrenceStart));
  return all;
}

const WEEKDAY_LABELS: Record<string, string> = {
  MO: "Monday",
  TU: "Tuesday",
  WE: "Wednesday",
  TH: "Thursday",
  FR: "Friday",
  SA: "Saturday",
  SU: "Sunday",
};

/** Human-readable summary of an RRULE fragment, e.g. "Every Tuesday and Thursday". */
export function describeRRule(rrule: string | null, recurrenceEndAt: string | null): string {
  if (!rrule) return "";
  const params = new URLSearchParams(rrule.replace(/;/g, "&"));
  const freq = params.get("FREQ");
  const byday = params.get("BYDAY");
  const interval = Number(params.get("INTERVAL") ?? "1");

  let base = "";
  if (freq === "WEEKLY" && byday) {
    const days = byday.split(",").map((d) => WEEKDAY_LABELS[d] ?? d);
    const joined =
      days.length <= 1
        ? days[0]
        : `${days.slice(0, -1).join(", ")} and ${days[days.length - 1]}`;
    base = interval > 1 ? `Every ${interval} weeks on ${joined}` : `Every ${joined}`;
  } else if (freq === "DAILY") {
    base = interval > 1 ? `Every ${interval} days` : "Every day";
  } else if (freq === "MONTHLY") {
    base = interval > 1 ? `Every ${interval} months` : "Every month";
  } else {
    base = "Repeats";
  }

  if (recurrenceEndAt) {
    // Explicit timeZone so this renders the same recurrence end date everywhere,
    // regardless of the host's or viewer's own system timezone (see shared/timezone.ts).
    const formatted = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Chicago",
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(recurrenceEndAt));
    base += ` until ${formatted}`;
  }
  return base;
}
